import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Chip,
  Divider,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  FileCopy as FileCopyIcon,
  CheckCircleOutline as CheckIcon,
  ErrorOutline as ErrorIcon,
  Warning as WarningIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      borderRadius: 16,
      minWidth: 520,
      maxWidth: 640,
    },
  },
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    paddingBottom: theme.spacing(1),
  },
  titleIcon: {
    color: theme.palette.primary.main,
    fontSize: 28,
  },
  stepper: {
    padding: theme.spacing(2, 0),
    backgroundColor: "transparent",
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: 14,
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    "&::before": {
      content: '""',
      width: 3,
      height: 18,
      backgroundColor: theme.palette.primary.main,
      borderRadius: 2,
    },
  },
  sourceInfo: {
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
  },
  sourceLabel: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  sourceName: {
    fontSize: 15,
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  optionRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(0.75, 0),
  },
  reviewItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 0),
  },
  resultSection: {
    padding: theme.spacing(2),
    borderRadius: 10,
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.divider}`,
  },
  warningBox: {
    padding: theme.spacing(1.5, 2),
    borderRadius: 8,
    backgroundColor: "rgba(255, 152, 0, 0.08)",
    border: "1px solid rgba(255, 152, 0, 0.3)",
    marginTop: theme.spacing(1.5),
  },
  successBox: {
    padding: theme.spacing(2),
    borderRadius: 10,
    backgroundColor: "rgba(76, 175, 80, 0.08)",
    border: "1px solid rgba(76, 175, 80, 0.3)",
    textAlign: "center",
  },
}));

const CHANNEL_LABELS = {
  whatsapp: "WhatsApp (Baileys)",
  wuzapi: "WhatsMeow (WuzAPI)",
  whatsapp_oficial: "WhatsApp Oficial",
  facebook: "Facebook",
  instagram: "Instagram",
  webchat: "WebChat",
  telegram: "Telegram",
};

const CLONE_OPTIONS = [
  { key: "queues", label: "Filas vinculadas", defaultOn: true },
  { key: "messages", label: "Mensagens automaticas (saudacao, ausencia, encerramento, ferias)", defaultOn: true },
  { key: "businessHours", label: "Horarios de atendimento", defaultOn: true },
  { key: "flows", label: "Flows / Chatbot", defaultOn: true },
  { key: "automations", label: "Automacoes de atendimento (tempos, bot, inatividade)", defaultOn: true },
  { key: "callReject", label: "Rejeicao de chamadas", defaultOn: true },
  { key: "generalSettings", label: "Configuracoes gerais (grupo, cor, etc.)", defaultOn: true },
];

const STEPS = ["Basico", "O que copiar", "Revisao"];

const CloneConnectionModal = ({ open, onClose, sourceWhatsapp }) => {
  const classes = useStyles();
  const [activeStep, setActiveStep] = useState(0);
  const [name, setName] = useState("");
  const [channel, setChannel] = useState("");
  const [options, setOptions] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Reset state when opening
  useEffect(() => {
    if (open && sourceWhatsapp) {
      setActiveStep(0);
      setName(`${sourceWhatsapp.name} - copia`);
      setChannel(sourceWhatsapp.channel || "whatsapp");
      const defaults = {};
      CLONE_OPTIONS.forEach((opt) => {
        defaults[opt.key] = opt.defaultOn;
      });
      setOptions(defaults);
      setResult(null);
    }
  }, [open, sourceWhatsapp]);

  const handleToggleOption = (key) => {
    setOptions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleClone = async () => {
    if (!name.trim()) {
      toast.error("Informe o nome da nova conexao.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post(`/whatsapp/${sourceWhatsapp.id}/clone`, {
        name: name.trim(),
        channel,
        options,
      });
      setResult(data);
      toast.success(`Conexao "${data.whatsapp.name}" criada com sucesso!`);
      setActiveStep(3); // success step
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setResult(null);
    setActiveStep(0);
    onClose();
  };

  if (!sourceWhatsapp) return null;

  const channelChanged = channel !== sourceWhatsapp.channel;

  const renderStep0 = () => (
    <>
      <Typography className={classes.sectionTitle}>Conexao origem</Typography>
      <Box className={classes.sourceInfo}>
        <Typography className={classes.sourceLabel}>Clonando a partir de:</Typography>
        <Box display="flex" alignItems="center" gap={1} style={{ gap: 8, marginTop: 4 }}>
          <Typography className={classes.sourceName}>{sourceWhatsapp.name}</Typography>
          <Chip
            size="small"
            label={CHANNEL_LABELS[sourceWhatsapp.channel] || sourceWhatsapp.channel}
            variant="outlined"
            style={{ fontSize: 11 }}
          />
        </Box>
      </Box>

      <Typography className={classes.sectionTitle}>Nova conexao</Typography>
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        label="Nome da nova conexao"
        value={name}
        onChange={(e) => setName(e.target.value)}
        style={{ marginBottom: 16 }}
        autoFocus
      />

      <FormControl variant="outlined" size="small" fullWidth>
        <InputLabel>Canal / Provedor</InputLabel>
        <Select
          value={channel}
          onChange={(e) => setChannel(e.target.value)}
          label="Canal / Provedor"
        >
          <MenuItem value="whatsapp">WhatsApp (Baileys)</MenuItem>
          <MenuItem value="wuzapi">WhatsMeow (WuzAPI)</MenuItem>
          <MenuItem value="whatsapp_oficial">WhatsApp Oficial</MenuItem>
          <MenuItem value="webchat">WebChat</MenuItem>
        </Select>
      </FormControl>

      {channelChanged && (
        <Box className={classes.warningBox} style={{ marginTop: 16 }}>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <WarningIcon style={{ color: "#FF9800", fontSize: 20 }} />
            <Typography variant="body2" style={{ fontWeight: 500 }}>
              Canal diferente da origem. Configuracoes incompativeis serao ignoradas.
            </Typography>
          </Box>
        </Box>
      )}
    </>
  );

  const renderStep1 = () => (
    <>
      <Typography className={classes.sectionTitle}>Selecione o que copiar</Typography>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
        Dados sensiveis (sessao, tokens, QRCode, numero) nunca sao copiados.
      </Typography>
      {CLONE_OPTIONS.map((opt) => (
        <Box key={opt.key} className={classes.optionRow}>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!options[opt.key]}
                onChange={() => handleToggleOption(opt.key)}
                color="primary"
                size="small"
              />
            }
            label={
              <Typography variant="body2" style={{ fontWeight: 500 }}>
                {opt.label}
              </Typography>
            }
          />
        </Box>
      ))}
    </>
  );

  const renderStep2 = () => {
    const selectedOpts = CLONE_OPTIONS.filter((opt) => options[opt.key]);
    const skippedOpts = CLONE_OPTIONS.filter((opt) => !options[opt.key]);

    return (
      <>
        <Typography className={classes.sectionTitle}>Confirme a clonagem</Typography>

        <Box className={classes.sourceInfo}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography className={classes.sourceLabel}>Origem</Typography>
              <Typography variant="body2" style={{ fontWeight: 600 }}>
                {sourceWhatsapp.name}
              </Typography>
            </Box>
            <ArrowForwardIcon color="action" />
            <Box style={{ textAlign: "right" }}>
              <Typography className={classes.sourceLabel}>Nova conexao</Typography>
              <Typography variant="body2" style={{ fontWeight: 600 }}>
                {name || "Sem nome"}
              </Typography>
            </Box>
          </Box>
          <Divider style={{ margin: "8px 0" }} />
          <Typography className={classes.sourceLabel}>
            Canal: {CHANNEL_LABELS[channel] || channel}
            {channelChanged && " (diferente da origem)"}
          </Typography>
        </Box>

        <Typography variant="subtitle2" style={{ fontWeight: 700, marginTop: 16, marginBottom: 8 }}>
          Sera copiado:
        </Typography>
        {selectedOpts.map((opt) => (
          <Box key={opt.key} className={classes.reviewItem}>
            <CheckIcon style={{ fontSize: 18, color: "#4CAF50" }} />
            <Typography variant="body2">{opt.label}</Typography>
          </Box>
        ))}

        {skippedOpts.length > 0 && (
          <>
            <Typography variant="subtitle2" style={{ fontWeight: 700, marginTop: 16, marginBottom: 8, color: "#999" }}>
              Nao sera copiado:
            </Typography>
            {skippedOpts.map((opt) => (
              <Box key={opt.key} className={classes.reviewItem}>
                <ErrorIcon style={{ fontSize: 18, color: "#bbb" }} />
                <Typography variant="body2" color="textSecondary">
                  {opt.label}
                </Typography>
              </Box>
            ))}
          </>
        )}
      </>
    );
  };

  const renderResult = () => {
    if (!result) return null;
    return (
      <>
        <Box className={classes.successBox}>
          <CheckIcon style={{ fontSize: 48, color: "#4CAF50", marginBottom: 8 }} />
          <Typography variant="h6" style={{ fontWeight: 700, marginBottom: 4 }}>
            Conexao criada!
          </Typography>
          <Typography variant="body2" color="textSecondary">
            A nova conexao "{result.whatsapp.name}" foi criada com as configuracoes da conexao original.
            Agora conecte o novo numero para comecar a usar.
          </Typography>
        </Box>

        {result.copiedItems && result.copiedItems.length > 0 && (
          <Box style={{ marginTop: 16 }}>
            <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
              Itens copiados:
            </Typography>
            {result.copiedItems.map((item, i) => (
              <Box key={i} className={classes.reviewItem}>
                {item.copied ? (
                  <CheckIcon style={{ fontSize: 16, color: "#4CAF50" }} />
                ) : (
                  <ErrorIcon style={{ fontSize: 16, color: "#bbb" }} />
                )}
                <Typography variant="body2" style={{ flex: 1 }}>
                  {item.item}
                </Typography>
                {!item.copied && item.reason && (
                  <Typography variant="caption" color="textSecondary">
                    {item.reason}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}

        {result.warnings && result.warnings.length > 0 && (
          <Box className={classes.warningBox} style={{ marginTop: 16 }}>
            <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 4, color: "#e65100" }}>
              Avisos:
            </Typography>
            {result.warnings.map((w, i) => (
              <Typography key={i} variant="body2" style={{ color: "#e65100" }}>
                {w}
              </Typography>
            ))}
          </Box>
        )}
      </>
    );
  };

  const isLastStep = activeStep === 2;
  const isResultStep = activeStep === 3;

  return (
    <Dialog open={open} onClose={handleClose} className={classes.dialog} maxWidth="sm" fullWidth>
      <DialogTitle disableTypography>
        <Box className={classes.dialogTitle}>
          <FileCopyIcon className={classes.titleIcon} />
          <Typography variant="h6" style={{ fontWeight: 700 }}>
            Clonar conexao
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {!isResultStep && (
          <Stepper activeStep={activeStep} className={classes.stepper} alternativeLabel>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        {activeStep === 0 && renderStep0()}
        {activeStep === 1 && renderStep1()}
        {activeStep === 2 && renderStep2()}
        {activeStep === 3 && renderResult()}
      </DialogContent>

      <DialogActions style={{ padding: "16px 24px" }}>
        {isResultStep ? (
          <Button onClick={handleClose} variant="contained" color="primary">
            Fechar
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={loading}>
              Cancelar
            </Button>
            {activeStep > 0 && (
              <Button
                onClick={() => setActiveStep((s) => s - 1)}
                startIcon={<ArrowBackIcon />}
                disabled={loading}
              >
                Voltar
              </Button>
            )}
            {!isLastStep ? (
              <Button
                variant="contained"
                color="primary"
                onClick={() => setActiveStep((s) => s + 1)}
                endIcon={<ArrowForwardIcon />}
                disabled={!name.trim()}
              >
                Proximo
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={handleClone}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <FileCopyIcon />}
              >
                {loading ? "Clonando..." : "Clonar conexao"}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CloneConnectionModal;
